import type { ReactNode } from 'react';

import { CheckBox } from './CheckBox';
import { cx } from './classNames';
import { NumericTextBox } from './NumericTextBox';
import type { SimpleTableColumn } from './SimpleTable';
import { getFieldValue, toText, withField } from './simpleTableUtils';
import { TextBox } from './TextBox';

export interface SimpleTableCellProps<T> {
  row: T;
  column: SimpleTableColumn<T>;
  editable: boolean;
  editing: boolean;
  selected: boolean;
  onSelect: () => void;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onCommit: (updated: T) => void;
}

export function SimpleTableCell<T>(props: SimpleTableCellProps<T>) {
  const { column, onCommit, row } = props;
  const value = getFieldValue(row, column.field);

  const editor = (): ReactNode => {
    if (column.editor !== undefined) return column.editor(row, onCommit);

    const apply = (next: unknown) => {
      onCommit(withField(row, column.field, next));
    };
    if (typeof value === 'boolean') return <CheckBox checked={value} onChange={apply} />;
    if (typeof value === 'number') {
      return (
        <NumericTextBox
          value={value}
          format={Number.isInteger(value) ? 'd' : 'N2'}
          onChange={apply}
        />
      );
    }
    return <TextBox value={toText(value)} onChange={apply} />;
  };

  const display = (): ReactNode => {
    if (column.template !== undefined) return column.template(row);
    if (typeof value === 'boolean') return <CheckBox checked={value} />;
    return toText(value);
  };

  // Die Zelle kürzt lange Werte mit `text-overflow: ellipsis` (REQ-17 §3); der
  // vollständige Wert bleibt über `title` lesbar. Eigene Anzeigen (`template`)
  // und Kästchen bringen ihre Beschriftung selbst mit.
  const title =
    column.template === undefined && typeof value !== 'boolean' ? toText(value) : undefined;

  return (
    <td
      title={title === '' ? undefined : title}
      className={cx(
        'e-rowcell',
        props.selected && 'e-cellselectionbackground',
        props.editing && 'e-editedbatchcell',
      )}
      onClick={() => {
        if (!props.editing) props.onSelect();
      }}
      onDoubleClick={() => {
        if (props.editable) props.onStartEdit();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') props.onCancelEdit();
      }}
    >
      {props.editing ? editor() : display()}
    </td>
  );
}

export default SimpleTableCell;
